import React, {
  useState,
  useMemo,
  Fragment,
  memo,
  useEffect,
  useCallback,
  useContext,
} from "react";
import Countdown from "react-countdown";
import _ from "lodash";
import {
  useTable,
  usePagination,
  useFilters,
  useGlobalFilter,
  useSortBy,
  useRowSelect,
} from "react-table";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { format } from "date-fns";
import Swal from "sweetalert2";
import { faAngleUp, faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog } from "@headlessui/react";
import { API, Auth, Storage } from "aws-amplify";

const Row = ({ row, i, value, getData, where }) => {
  const [error, setError] = useState(false);
  const [form, setForm] = useState({
    value: value.value,
  });
  const [showModal, setShowModal] = useState(false);

  const onSubmit = async () => {
    try {
      const result = await Swal.fire({
        title: "คุณยืนยันที่จะอนุมัตินี้หรือไม่ ?",
        showCancelButton: true,
        confirmButtonText: `บันทึก`,
        cancelButtonText: "ยกเลิก",
        showLoaderOnConfirm: true,
        showLoaderOnDeny: false,
        preConfirm: async () => {
          if (!form.value) {
            setError(true);
            return {
              error: true,
            };
          }
          await API.put("MoneyHubAdmin", "/point", {
            body: {
              type: value.type,
              value: +form.value,
            },
            headers: {
              Authorization: `Bearer ${(await Auth.currentSession())
                .getIdToken()
                .getJwtToken()}`,
            },
          });
        },
      });
      if (result.value?.error) {
        return await Swal.fire({
          title: "WARNING",
          text: `โปรดกรอกข้อมูลคะแนนได้ที่`,
          icon: "warning",
          confirmButtonText: "ยืนยัน",
        });
      }
      if (!result.isConfirmed) return;
      await Swal.fire({
        title: "SUCCESS",
        text: "อัพเดตข้อมูลสำเร็จ",
        icon: "success",
        confirmButtonText: "ยืนยัน",
      });
      await getData(where);
    } catch (error) {
      console.log("Error", error);
      await Swal.fire({
        title: "ERROR",
        text: "Something went wrong",
        icon: "error",
        confirmButtonText: "ยืนยัน",
      });
    }
  };

  return (
    <Fragment>
      <tr
        {...row.getRowProps()}
        onClick={() => setShowModal(true)}
        className={classNames(
          "cursor-pointer hover:bg-blueGray-200 transition-all duration-300",
          {
            "bg-white": i % 2 === 0,
            "bg-gray-100": i % 2 !== 0,
          }
        )}
      >
        {row.cells.map((cell, index) => {
          return (
            <td
              className={"p-4 text-gray-700 text-sm text-center"}
              {...cell.getCellProps()}
            >
              {cell.render("Cell")}
            </td>
          );
        })}
      </tr>
      <Dialog
        open={showModal}
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={() => setShowModal(false)}
      >
        <div className="flex items-center justify-center min-h-screen">
          <div className="bg-white border border-gray-300 rounded max-w-sm mx-auto p-4 z-20">
            <Dialog.Title className="font-black text-lg">
              {value.type}
            </Dialog.Title>
            <Dialog.Description className="my-4">
              <div>
                <div>คะแนนที่ได้</div>
                <input
                  className="border border-gray-300 focus:outline-none px-3 py-2 rounded"
                  type="text"
                  name="value"
                  value={form.value}
                  onChange={(e) => setForm({ value: e.target.value })}
                />
                <span className="text-red-400 mx-2">
                  {error && !form.detail && "โปรดใส่รายละเอียด"}
                </span>
              </div>
            </Dialog.Description>
            <button
              className="p-2 bg-blue-500 text-white w-20 mr-4 rounded"
              onClick={onSubmit}
            >
              ตกลง
            </button>
            <button
              className="p-2 text-blue-500 border border-blue-500 w-20 rounded"
              onClick={() => setShowModal(false)}
            >
              ยกเลิก
            </button>
          </div>
        </div>
      </Dialog>
    </Fragment>
  );
};

function PointTable({ data, getData, where }) {
  const { user } = useSelector((state) => state.initializeApp);

  const columns = useMemo(
    () => [
      {
        Header: "ประเภท",
        accessor: "type",
      },
      {
        Header: "คะแนนที่ได้",
        accessor: "value",
        Cell: ({ row }) => {
          return <div>{row.values.value}</div>;
        },
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
  } = useTable({
    columns,
    data,
  });

  return (
    <div className="mx-4 mb-2">
      <div className="bg-white rounded border border-gray-300 w-full overflow-x-auto ">
        <table {...getTableProps()} className="w-full whitespace-no-wrap">
          <thead className=" text-sm font-semibold tracking-wide text-gray-500  border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, i) => (
                  <th
                    {...column.getHeaderProps()}
                    className={classNames("px-4 py-3 text-center", {
                      "rounded-tl": i === 0,
                      "rounded-tr": i === headerGroup.headers.length - 1,
                    })}
                  >
                    {column.render("Header")}
                    <span>
                      {" "}
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon icon={faAngleDown} />
                        ) : (
                          <FontAwesomeIcon icon={faAngleUp} />
                        )
                      ) : (
                        ""
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            className="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800 text-gray-700 dark:text-gray-400"
            {...getTableBodyProps()}
          >
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <Row
                  where={where}
                  key={row.getRowProps().key}
                  i={i}
                  row={row}
                  value={data[i]}
                  getData={getData}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PointTable;
